import { useContext } from 'react';
import { useTheme } from '@emotion/react';
import { createTheme, ThemeProvider } from '@mui/material';
import Box from '@mui/material/Box';
import MuiTab from '@mui/material/Tab';
import MuiTabs from '@mui/material/Tabs';
import { AppThemingContext } from '../../containers/AppTheming/Context';

export type TabsBarItem = {
  label: string;
  value: string;
  /** Route-driven tab. Only rendered as a real link when the caller supplies `linkComponent`
   * (see its doc comment) — otherwise it's inert aside from `onChange`. */
  to?: string;
};

type Props = {
  currentTab: string;
  items: TabsBarItem[];
  /** Called on click for items with no `to` — a route-driven item relies on navigation (and the
   * caller re-rendering with a new `currentTab`) instead. */
  onChange?: (value: string) => void;
  /**
   * The app's own router `Link` (e.g. react-router-dom's), used to render items that set `to`.
   * Deliberately not hard-coded to chat-components' own cross-app `Link` — that component's
   * navigation goes through a `navigate()` captured once via `useNavigate()` wherever the app
   * wires up `ChatRoutingProvider` (typically near the app root), so a *relative* `to` (e.g.
   * `'entities'`) resolves relative to that call site, not to wherever this tab bar is actually
   * rendered in the route tree — it silently navigates to the wrong place for nested routes.
   * react-router-dom's real `Link`, by contrast, resolves a relative `to` against its own
   * rendered position, which is what a route-driven tab bar like apps/chat's project tabs needs.
   */
  linkComponent?: React.ElementType;
  className?: string;
};

/**
 * The underline MUI tab bar apps/chat's project-page tabs use (`TabsContainer` in
 * apps/chat/src/components/TabView/Tabs.tsx), extracted so a non-routed consumer — e.g.
 * SegmentProfile's Profile/Avatar Settings switch — can render the identical look without needing
 * route targets for every tab. `TabsContainer` itself is kept as a thin wrapper over this
 * component (passing its own `Link`) so both stay visually identical by construction, not by
 * copy-pasted styling.
 */
export const TabsBar = ({ currentTab, items, onChange, linkComponent, className }: Props) => {
  const originalTheme = useTheme();
  const themingContext = useContext(AppThemingContext);

  const theme = createTheme({
    ...originalTheme,
    palette: {
      ...originalTheme.palette,
      primary: {
        ...originalTheme.palette.primary,
        main: themingContext?.enabled ? themingContext.palette.primary?.main : originalTheme.palette.hyperlink,
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <Box className={className} sx={{ position: 'relative' }}>
        <Bar />
        <MuiTabs
          indicatorColor="primary"
          textColor="primary"
          value={currentTab}
          onChange={(_event, value: string) => onChange?.(value)}
          sx={{
            '& .MuiTabs-indicator': {
              height: '4px',
              position: 'absolute',
              bottom: 1,
              zIndex: 1,
            },
          }}
        >
          {items.map((item) => (
            <StyledTab
              key={item.value}
              label={item.label}
              value={item.value}
              to={item.to}
              linkComponent={linkComponent}
            />
          ))}
        </MuiTabs>
      </Box>
    </ThemeProvider>
  );
};

const Bar = (props: React.ComponentProps<typeof Box>) => (
  <Box
    {...props}
    sx={{
      position: 'absolute',
      bottom: 1,
      width: '100%',
      height: '3px',
      backgroundColor: '#e5e5e5',
    }}
  />
);

type StyledTabProps = {
  label: string;
  value: string;
  to?: string;
  linkComponent?: React.ElementType;
};

const StyledTab = ({ to, linkComponent, ...props }: StyledTabProps) => (
  <MuiTab
    {...props}
    disableRipple
    {...(to && linkComponent ? { component: linkComponent, to } : {})}
    sx={(theme) => ({
      fontFamily: theme.fonts.bold,
      textTransform: 'none',
      fontSize: 16,
      width: 150,
      minHeight: 45,
    })}
  />
);
